<template>
  <div class="profile-page-wrapper">
    <div class="profile-container">
      <!-- Left Sidebar Navigation -->
      <div class="profile-sidebar">
        <div class="user-brief">
          <div class="avatar-wrapper">
            <Avatar :src="user.avatar || defaultAvatar" size="large" class="sidebar-avatar" />
            <div class="avatar-overlay">
              <Upload :action="uploadUrl" :headers="uploadHeaders" :data="{ type: 'avatar' }"
                :on-success="handleAvatarSuccess" :show-upload-list="false" accept="image/*">
                <Icon type="ios-camera" size="24" />
              </Upload>
            </div>
          </div>
          <h3 class="sidebar-username">{{ user.username }}</h3>
          <p class="sidebar-role">{{ user.role === 'admin' ? '超级管理员' : '高级教师' }}</p>
        </div>

        <Menu :active-name="activeTab" theme="light" width="auto" @on-select="handleTabChange" class="sidebar-menu">
          <MenuItem name="profile">
          <Icon type="ios-contact-outline" />
          个人资料
          </MenuItem>
          <MenuItem name="security">
          <Icon type="ios-lock-outline" />
          安全设置
          </MenuItem>
          <MenuItem name="favorites">
          <Icon type="ios-star-outline" />
          收藏夹
          </MenuItem>
          <MenuItem name="downloads">
          <Icon type="ios-cloud-download-outline" />
          下载记录
          </MenuItem>
          <MenuItem name="points">
          <Icon type="md-coin" />
          积分与邀请
          </MenuItem>
        </Menu>

        <div class="sidebar-footer">
          <Button type="text" icon="ios-log-out" @click="handleLogout" class="logout-btn">退出登录</Button>
        </div>
      </div>

      <!-- Right Main Content -->
      <div class="profile-main">
        <div class="content-card glass-effect">
          <transition name="fade-slide" mode="out-in">
            <!-- Personal Information Section -->
            <div v-if="activeTab === 'profile'" key="profile" class="section-content">
              <div class="section-header">
                <h2>个人资料</h2>
                <p>管理您的个人基本信息，让更多人了解您</p>
              </div>

              <Form :model="profileForm" label-position="top" class="custom-form">
                <Row :gutter="32">
                  <Col span="12">
                  <FormItem label="用户名">
                    <Input v-model="user.username" disabled size="large" prefix="ios-person-outline" />
                  </FormItem>
                  </Col>
                  <Col span="12">
                  <FormItem label="当前角色">
                    <Input v-model="user.role" disabled size="large" prefix="ios-bowtie-outline" />
                  </FormItem>
                  </Col>
                </Row>

                <Row :gutter="32">
                  <Col span="12">
                  <FormItem label="学校名称">
                    <Input v-model="profileForm.school" placeholder="输入所在学校" size="large" prefix="ios-school-outline" />
                  </FormItem>
                  </Col>
                  <Col span="12">
                  <FormItem label="教学科目">
                    <Input v-model="profileForm.subject" placeholder="输入教学科目" size="large" prefix="ios-book-outline" />
                  </FormItem>
                  </Col>
                </Row>

                <FormItem label="授课年级">
                  <Input v-model="profileForm.grade" placeholder="输入所在年级" size="large" prefix="ios-ribbon-outline" />
                </FormItem>

                <FormItem label="个人简介">
                  <Input v-model="profileForm.bio" type="textarea" :autosize="{ minRows: 3, maxRows: 6 }"
                    placeholder="介绍一下自己吧，让学生和同事更了解您..." />
                </FormItem>

                <div class="form-actions">
                  <Button type="primary" size="large" @click="updateProfile" :loading="profileLoading" class="save-btn">
                    保存更改
                  </Button>
                </div>
              </Form>
            </div>

            <!-- Security Settings Section -->
            <div v-else-if="activeTab === 'security'" key="security" class="section-content">
              <div class="section-header">
                <h2>安全设置</h2>
                <p>定期更换密码有助于保护您的账号安全</p>
              </div>

              <Form :model="passwordForm" :rules="passwordRules" ref="passwordForm" label-position="top"
                class="custom-form mt-20">
                <FormItem label="当前密码" prop="oldPassword">
                  <Input type="password" v-model="passwordForm.oldPassword" placeholder="请输入当前密码" size="large"
                    prefix="ios-unlock-outline" password />
                </FormItem>
                <FormItem label="新密码" prop="newPassword">
                  <Input type="password" v-model="passwordForm.newPassword" placeholder="请输入新密码（至少6位）" size="large"
                    prefix="ios-key-outline" password />
                </FormItem>
                <FormItem label="确认新密码" prop="confirmPassword">
                  <Input type="password" v-model="passwordForm.confirmPassword" placeholder="请再次输入新密码" size="large"
                    prefix="ios-checkmark-circle-outline" password />
                </FormItem>

                <div class="form-actions">
                  <Button type="primary" size="large" @click="changePassword" :loading="passwordLoading"
                    class="save-btn">
                    修改密码
                  </Button>
                </div>
              </Form>
            </div>

            <!-- Favorites Section (Mock) -->
            <div v-else-if="activeTab === 'favorites'" key="favorites" class="section-content">
              <div class="section-header">
                <h2>我的收藏</h2>
                <p>这里显示您收藏的所有题目和试卷</p>
              </div>

              <div class="empty-state">
                <Icon type="ios-star-outline" size="64" color="#dcdee2" />
                <p>暂无收藏记录</p>
                <Button type="primary" ghost @click="$router.push('/papers')">去试卷库看看</Button>
              </div>
            </div>

            <div v-else-if="activeTab === 'downloads'" key="downloads" class="section-content">
              <div class="section-header">
                <h2>下载记录</h2>
                <p>查看您近期导出的试卷记录</p>
              </div>

              <div v-if="downloadLoading" style="text-align:center; padding:40px;">
                <Spin size="large" />
              </div>
              <div v-else-if="downloadRecords.length === 0" class="empty-state">
                <Icon type="ios-cloud-download-outline" size="64" color="#dcdee2" />
                <p>暂无下载记录</p>
                <Button type="primary" ghost @click="$router.push('/papers')">去试卷库看看</Button>
              </div>
              <div v-else>
                <!-- Custom Papers Section -->
                <div class="download-group" v-if="customPaperDownloads.length > 0">
                  <Divider orientation="left" class="group-divider">组卷记录 ({{ customPaperDownloads.length }})</Divider>
                  <List size="small" class="download-list">
                    <ListItem v-for="item in customPaperDownloads" :key="item.id">
                      <ListItemMeta :title="getDownloadTitle(item)" :description="formatDownloadTime(item.created_at)">
                        <template #avatar>
                          <Avatar icon="ios-hammer" style="background-color: #f8f8f9; color: #19be6b" />
                        </template>
                      </ListItemMeta>
                      <template #action>
                        <li>
                          <Button type="text" size="small" icon="ios-download-outline"
                            @click="reDownload(item)">再次下载</Button>
                        </li>
                      </template>
                    </ListItem>
                  </List>
                </div>

                <!-- Regular Papers Section -->
                <div class="download-group" v-if="paperDownloads.length > 0">
                  <Divider orientation="left" class="group-divider">试卷记录 ({{ paperDownloads.length }})</Divider>
                  <List size="small" class="download-list">
                    <ListItem v-for="item in paperDownloads" :key="item.id">
                      <ListItemMeta :title="getDownloadTitle(item)" :description="formatDownloadTime(item.created_at)">
                        <template #avatar>
                          <Avatar icon="ios-paper" style="background-color: #f8f8f9; color: #2d8cf0" />
                        </template>
                      </ListItemMeta>
                      <template #action>
                        <li>
                          <Button type="text" size="small" icon="ios-download-outline"
                            @click="reDownload(item)">再次下载</Button>
                        </li>
                      </template>
                    </ListItem>
                  </List>
                </div>
              </div>
            </div>

            <!-- Points Section -->
            <div v-else-if="activeTab === 'points'" key="points" class="section-content">
              <div class="section-header">
                <h2>积分与邀请</h2>
                <p>管理您的账户积分，参与签到，查看邀请奖励</p>
              </div>

              <div class="points-summary-card">
                <Row :gutter="16">
                  <Col span="12">
                  <div class="summary-item">
                    <div class="summary-label">当前积分</div>
                    <div class="summary-value">{{ userPoints.total_points || 0 }}</div>
                  </div>
                  </Col>
                  <Col span="12">
                  <div class="summary-item">
                    <div class="summary-label">连续签到</div>
                    <div class="summary-value">{{ userPoints.checkin_streak || 0 }} 天</div>
                  </div>
                  </Col>
                </Row>
                <div class="summary-actions">
                  <Button type="primary" shape="circle" icon="md-checkmark-circle"
                    :disabled="userPoints.has_checked_in_today" @click="goToPointsCenter">
                    {{ userPoints.has_checked_in_today ? '今日已签到' : '立即签到' }}
                  </Button>
                  <Button type="text" @click="goToPointsCenter">查看更多明细
                    <Icon type="ios-arrow-forward" />
                  </Button>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api, { getUser, setUser, getToken, logout } from '@/utils/api';
import { downloadFile } from '@/utils/download';

export default {
  name: "UserProfile",
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error("两次输入的密码不一致"));
      } else {
        callback();
      }
    };
    return {
      activeTab: 'profile',
      user: {
        username: "",
        role: "",
        avatar: "",
        school: "",
        subject: "",
        grade: "",
        bio: "",
      },
      profileForm: {
        school: "",
        subject: "",
        grade: "",
        bio: "",
      },
      passwordForm: {
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
      passwordRules: {
        oldPassword: [{ required: true, message: "请输入旧密码", trigger: "blur" }],
        newPassword: [
          { required: true, message: "请输入新密码", trigger: "blur" },
          { min: 6, message: "密码至少6个字符", trigger: "blur" },
        ],
        confirmPassword: [
          { required: true, message: "请确认密码", trigger: "blur" },
          { validator: validateConfirmPassword, trigger: "blur" },
        ],
      },
      uploadUrl: "/api/upload/image",
      profileLoading: false,
      passwordLoading: false,
      downloadLoading: false,
      downloadRecords: [],
      userPoints: {
        total_points: 0,
        checkin_streak: 0,
        has_checked_in_today: false
      },
      defaultAvatar: 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png',
    };
  },
  computed: {
    uploadHeaders() {
      const token = getToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    paperDownloads() {
      return this.downloadRecords.filter(item => item.resource_type === 'paper');
    },
    customPaperDownloads() {
      return this.downloadRecords.filter(item => item.resource_type === 'custom_paper');
    },
  },
  mounted() {
    this.loadUserProfile();
  },
  methods: {
    handleTabChange(name) {
      this.activeTab = name;
      if (name === 'downloads' && this.downloadRecords.length === 0) {
        this.fetchDownloadRecords();
      }
      if (name === 'points') {
        this.fetchUserPoints();
      }
    },
    async loadUserProfile() {
      // First try to get from localStorage
      const cachedUser = getUser();
      if (cachedUser) {
        this.user = cachedUser;
        this.profileForm.school = this.user.school || "";
        this.profileForm.subject = this.user.subject || "";
        this.profileForm.grade = this.user.grade || "";
        this.profileForm.bio = this.user.bio || "";
      }

      // Then fetch fresh data from server
      try {
        const response = await api.get("/api/user/profile");
        this.user = response.data.user;
        this.profileForm.school = this.user.school || "";
        this.profileForm.subject = this.user.subject || "";
        this.profileForm.grade = this.user.grade || "";
        this.profileForm.bio = this.user.bio || "";
        // Update cached user
        setUser(this.user);
      } catch (error) {
        console.error("Load profile error:", error);
      }
    },
    async updateProfile() {
      this.profileLoading = true;
      try {
        const response = await api.put("/api/user/profile", this.profileForm);
        this.$Message.success("资料更新成功");
        this.user = { ...this.user, ...response.data.user };
        setUser(this.user);
      } catch (error) {
        console.error("Update profile error:", error);
        this.$Message.error(error.response?.data?.error || "更新失败");
      } finally {
        this.profileLoading = false;
      }
    },
    async changePassword() {
      this.$refs.passwordForm.validate(async (valid) => {
        if (!valid) return;
        this.passwordLoading = true;
        try {
          await api.put("/api/user/password", {
            oldPassword: this.passwordForm.oldPassword,
            newPassword: this.passwordForm.newPassword,
          });
          this.$Message.success("密码修改成功");
          this.passwordForm = { oldPassword: "", newPassword: "", confirmPassword: "" };
          this.$refs.passwordForm.resetFields();
        } catch (error) {
          console.error("Change password error:", error);
          this.$Message.error(error.response?.data?.error || "修改失败");
        } finally {
          this.passwordLoading = false;
        }
      });
    },
    handleAvatarSuccess(response) {
      if (response.url) {
        this.user.avatar = response.url;
        this.updateProfile();
        this.$Message.success("头像上传成功");
      }
    },
    handleLogout() {
      this.$Modal.confirm({
        title: "确认退出",
        content: "确定要退出登录吗？",
        onOk: () => {
          logout();
          this.$Message.success("已退出登录");
          this.$router.push('/home');
        },
      });
    },
    async fetchDownloadRecords() {
      this.downloadLoading = true;
      try {
        const res = await api.get('/api/user/download-logs');
        this.downloadRecords = res.data.logs || [];
      } catch (e) {
        console.error('Failed to fetch download records:', e);
      } finally {
        this.downloadLoading = false;
      }
    },
    getDownloadTitle(item) {
      if (item.resource_type === 'custom_paper' && item.resource_data) {
        try {
          const data = JSON.parse(item.resource_data);
          return data.title || '自定义组卷';
        } catch (e) {
          return '自定义组卷';
        }
      }
      return item.paper_name || `试卷 #${item.resource_id}`;
    },
    async reDownload(item) {
      if (item.resource_type === 'custom_paper' && item.resource_data) {
        try {
          const data = JSON.parse(item.resource_data);
          this.$Message.loading({ content: '正在准备下载...', duration: 0 });

          // Request a new temporary token for this paid resource?
          // Actually, our backend DownloadDocx needs downloadToken
          // We should either reuse the old one (if it's not expired) or have a way to generate a new one for paid orders.
          // Since the order is paid, we can just pass the order ID or something?
          // Let's use the original download_token if it's still there, or add a re-download helper on backend.

          const exportData = {
            ...data,
            downloadToken: item.download_token // Re-using the token from the order
          };

          const endpoint = '/api/downloadDocx'; // Default to docx for re-download for now
          const res = await api.post(endpoint, exportData, { responseType: 'blob' });
          downloadFile(res.data, `${data.title || '试卷'}.docx`);

          this.$Message.destroy();
          this.$Message.success('开始下载');
        } catch (e) {
          this.$Message.destroy();
          this.$Message.error('重试下载失败');
        }
      } else if (item.resource_id) {
        const token = getToken();
        // Regular paper download
        downloadFile(`/api/papers/${item.resource_id}/download?token=${token}&downloadToken=${item.download_token}`);
      } else {
        this.$Message.warning('无法确定下载来源');
      }
    },
    formatDownloadTime(ts) {
      if (!ts) return '';
      const d = new Date(ts);
      if (isNaN(d.getTime())) return String(ts);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    },
    async fetchUserPoints() {
      try {
        const res = await api.get('/api/points/balance');
        this.userPoints = res.data;
      } catch (e) {
        console.error('Failed to fetch user points:', e);
      }
    },
    goToPointsCenter() {
      this.$router.push('/points-center');
    }
  },
};
</script>

<style scoped>
.profile-page-wrapper {
  min-height: calc(100vh - 64px);
  background: #f0f2f5 url('../assets/logo.png') no-repeat center center fixed;
  background-size: cover;
  padding: 40px 0;
  position: relative;
}

.profile-page-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(240, 242, 245, 0.8);
  z-index: 0;
}

.profile-container {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  gap: 24px;
  min-height: 600px;
  position: relative;
  z-index: 1;
}

/* Sidebar Styling */
.profile-sidebar {
  width: 280px;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 16px;
  padding: 30px 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.1);
}

.user-brief {
  text-align: center;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
}

.avatar-wrapper {
  position: relative;
  display: inline-block;
  width: 90px;
  height: 90px;
  margin-bottom: 16px;
}

.sidebar-avatar {
  width: 90px;
  height: 90px;
  border: 4px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.avatar-overlay {
  position: absolute;
  bottom: 0;
  right: 0;
  background: #3b82f6;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px solid white;
  transition: transform 0.2s;
}

.avatar-overlay:hover {
  transform: scale(1.1);
}

.sidebar-username {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
}

.sidebar-role {
  font-size: 13px;
  color: #666;
}

.sidebar-menu {
  background: transparent;
  border-right: none;
}

.sidebar-menu :deep(.ivu-menu-item) {
  padding: 14px 24px;
  font-size: 14px;
  color: #515a6e;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s;
}

.sidebar-menu :deep(.ivu-menu-item:hover) {
  background: rgba(59, 130, 246, 0.05);
  color: #3b82f6;
}

.sidebar-menu :deep(.ivu-menu-item-active) {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  font-weight: 600;
}

.sidebar-menu :deep(.ivu-menu-item:after) {
  display: none;
}

.sidebar-footer {
  margin-top: auto;
  padding: 0 20px;
}

.logout-btn {
  color: #ed4014;
  width: 100%;
  text-align: left;
}

/* Main Content Styling */
.profile-main {
  flex: 1;
}

.content-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 16px;
  padding: 40px;
  height: 100%;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.08);
}

.section-header {
  margin-bottom: 32px;
}

.section-header h2 {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.section-header p {
  color: #808695;
  font-size: 14px;
}

.custom-form :deep(.ivu-form-item-label) {
  font-weight: 600;
  color: #495060;
  padding-bottom: 8px;
}

.form-actions {
  margin-top: 40px;
  display: flex;
  justify-content: flex-end;
}

.save-btn {
  min-width: 140px;
  border-radius: 20px;
  font-weight: 600;
  height: 40px;
}

/* Transitions */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: #808695;
  gap: 16px;
}

.download-list {
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  padding: 0 16px;
  background: rgba(255, 255, 255, 0.5);
}

.mt-20 {
  margin-top: 20px;
}

.points-summary-card {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(168, 85, 247, 0.1));
  padding: 30px;
  border-radius: 20px;
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.summary-item {
  text-align: center;
  margin-bottom: 24px;
}

.summary-label {
  font-size: 14px;
  color: #808695;
  margin-bottom: 8px;
}

.summary-value {
  font-size: 32px;
  font-weight: 800;
  color: #1a1a1a;
  font-family: 'Dosis', sans-serif;
}

.summary-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding-top: 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.download-group {
  margin-bottom: 30px;
}

.group-divider :deep(.ivu-divider-inner-text) {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  background: #fff;
  padding: 4px 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 20px;
}
</style>
